// import { format } from 'date-fns';

import { calcInitStartDate, calcInitEndDate, TODAY } from '../../../utilities';
import { TAB_NAME, updateOverlay, updateChartData, updateTitle, getInitTemp } from './fns';
import {
  whitePineNeedleDamageOptions,
  pestsAndPathogensAnnualOptions,
  pestsAndPathogensTimeseriesOptions,
  southernPineBeetlePastYearsOptions,
  beechLeafDiseaseOptions,
  hwaAnnualKillOptions,
  hwaDailyMintOptions
} from './charts';

import HoverPopper from '../../../components/hover-popper/hover-popper.component';

const poppers = {
  'capitalizedNormal': {
    hoverText: 'Normal',
    popperText: {
      'wpnd-chart-1': '"Normal" in this case is the annual precipitation from 1952 - Present',
      'wpnd-chart-2': '"Normal" in this case is the annual number of days with ≥0.01 inches of precipitation from 1952 - Present',
    }
  },
  'icon': {
    hover: undefined,
    popperText: {
      'wpnd-threshold': 'White Pine Needle Damage is influenced by precpitation. No thresholds are known, but an increase in precipitation indicated higher White Pine Needle Damage risk.',
      'hrd-threshold': 'Temperatures below -12°F kill the main agent of Heterobasidion Root Disease.',
      'pwd-threshold': 'Temperatures below 5°F kill the main agent of Pine Wilt Disease.',
      'spb-threshold': '10 years of air temperatures above 14°F can lead to emergence of Southern Pine Beetles.',
      'hwa-meanwt-threshold': 'A mean average daily temperature above 23°F through the winter can lead to emergence of Hemlock Woolly Adelgid.',
      'hwa-meandt-threshold': 'A mean minimum daily temperature below 14°F for a 93 day period can lead to death of Hemlock Woolly Adelgid.',
      'hwa-mint-threshold': 'Temperatures below -40°F can lead to death of Hemlock Woolly Adelgid.',
    }
  }
}
const generateHoverPopper = (popperType, indicatorName) => <HoverPopper key={popperType + indicatorName} popperText={poppers[popperType].popperText[indicatorName]}>{poppers[popperType].hoverText}</HoverPopper>;

const info = {
  name: TAB_NAME,
  subTabs: [{
    name: 'Beech Leaf Disease'
  },{
    name: 'Hemlock Woolly Adelgid'
  },{
    name: 'Heterobasidion Root Disease'
  },{
    name: 'Pine Wilt Disease'
  },{
    name: 'Southern Pine Beetle'
  },{
    name: 'White Pine Needle Damage'
  }],
  chartInfo: {
    chartSelectorId: 'pestsAndPathogensChartSelector',
    charts: [{
      name: 'Historic Defoliation Severity',
      showIfSubTab: ['White Pine Needle Damage'],
      func: (chartData, address, ...rest) => whitePineNeedleDamageOptions(chartData, address)
    },{
      name: 'Days with Min. Temperature Under Threshold Throughout Season',
      showIfSubTab: ['Southern Pine Beetle', 'Pine Wilt Disease', 'Heterobasidion Root Disease'],
      func: (chartData, address, tabInfoContextState, selectedSubTab) => {
        const reformattedName = selectedSubTab.split(' ').map(p => p.toLowerCase()).join('-') + '-threshold-selector';
        const tab = tabInfoContextState.tabInformation.find(({ name }) => name === TAB_NAME);
        const subTabChartOptions = tab.pageInfo[selectedSubTab].chart.options;
        const thresholdValue = subTabChartOptions.find(({ id }) => id === reformattedName).props.value;
        let threshold = typeof thresholdValue === 'function' ? thresholdValue({ selectedSubTab })[0] : thresholdValue[0];
        return pestsAndPathogensTimeseriesOptions(chartData, address, threshold, 'below');
      }
    },{
      name: 'Number of Times Min. Temperature was Under Threshold',
      showIfSubTab: ['Southern Pine Beetle'],
      func: (chartData, address, tabInfoContextState, ...rest) => southernPineBeetlePastYearsOptions(chartData, address, tabInfoContextState.tabsSharedState.pestsAndPathogensSeasonBoundsSelector.value)
    },{
      name: 'Number of Times Min. Temperature was Under Threshold',
      showIfSubTab: ['Pine Wilt Disease', 'Heterobasidion Root Disease'],
      func: (chartData, address, tabInfoContextState, ...rest) => pestsAndPathogensAnnualOptions(chartData, address, tabInfoContextState.tabsSharedState.pestsAndPathogensSeasonBoundsSelector.value)
    },{
      name: 'Probability of Beech Leaf Disease Based on Isothermality',
      showIfSubTab: ['Beech Leaf Disease'],
      func: (chartData, address, ...rest) => beechLeafDiseaseOptions(chartData, address, 'Isothermality')
    },{
      name: 'Probability of Beech Leaf Disease Based on Seasonality',
      showIfSubTab: ['Beech Leaf Disease'],
      func: (chartData, address, ...rest) => beechLeafDiseaseOptions(chartData, address, 'Seasonality')
    },{
      name: 'Daily Minimum Temperature through Winter',
      showIfSubTab: ['Hemlock Woolly Adelgid'],
      func: (chartData, address, ...rest) => hwaDailyMintOptions(chartData, address, rest)
    },{
      name: 'Annual Kill Events and Winter Temperatures',
      showIfSubTab: ['Hemlock Woolly Adelgid'],
      func: (chartData, address, ...rest) => hwaAnnualKillOptions(chartData, address)
    }],
  },
  onChange: [{
    whenXChanges: [
      'selectedSubTab',
      'wpndOverlaySelector',
      'hemlockWoollyAdelgidOverlayTypeSelector',
      'beechLeafDiseaseOverlayTypeSelector',
      'beechLeafDiseaseOverlayTimeframeSelector',
      'departureSelector',
      'spbMortalitySelector',
      'bldKnownCountiesToggle',
      'hwaKnownCountiesToggle',
      'biomassThresholdSelector'
    ],
    handleChange: updateOverlay,
    showLoading: true
  },{
    whenXChanges: [
      'pestsAndPathogensThresholdSelector',
      'pestsAndPathogensSeasonBoundsSelector',
      'rawChartData',
      'selectedSubTab',
      'southern-pine-beetle-threshold-selector',
      'pine-wilt-disease-threshold-selector',
      'heterobasidion-root-disease-threshold-selector',
      'beech-leaf-disease-seasonality-threshold-selector',
      'beech-leaf-disease-isothermality-threshold-selector',
      'hemlock-woolly-adelgid-meanwt-threshold-selector',
      'hemlock-woolly-adelgid-meandt-threshold-selector',
      'hemlock-woolly-adelgid-mint-threshold-selector'
    ],
    handleChange: updateChartData,
    showLoading: true
  }],
  mapInfo: {
    overlayKeySelectorId: (selectedSubTab) => {
      let selectorId = '';
      if (selectedSubTab === 'White Pine Needle Damage') {
        selectorId = 'wpndOverlaySelector';
      } else if (selectedSubTab === 'Beech Leaf Disease') {
        selectorId = 'beechLeafDiseaseOverlayTimeframeSelector';
      } else if (selectedSubTab === 'Hemlock Woolly Adelgid') {
        selectorId = 'hemlockWoollyAdelgidOverlayTypeSelector';
      } else {
        selectorId = 'departureSelector';
      }

      return selectorId;
    },
    regionsSelectorId: 'regionSelector'
  },
  optionsPanel: {
    map: [{
      component: 'toggleSelector',
      id: 'bld-known-counties-toggle',
      useSharedState: 'bldKnownCountiesToggle',
      showIfSubTab: ['Beech Leaf Disease']
    },{
      component: 'toggleSelector',
      id: 'hwa-known-counties-toggle',
      useSharedState: 'hwaKnownCountiesToggle',
      showIfSubTab: ['Hemlock Woolly Adelgid']
    },{
      component: 'listSelector',
      id: 'pests-and-pathogens-regions',
      useSharedState: 'regionSelector',
      props: {
        label: () => 'Select a Region'
      }
    },{
      component: 'radioSelector',
      id: 'pests-and-pathogens-comparison',
      useSharedState: 'departureSelector',
      showIfSubTab: ['Southern Pine Beetle', 'Pine Wilt Disease', 'Heterobasidion Root Disease']
    },{
      component: 'radioSelector',
      id: 'spb-mortality',
      useSharedState: 'spbMortalitySelector',
      showIfSubTab: ['Southern Pine Beetle']
    },{
      component: 'radioSelector',
      id: 'wpnd-overlay',
      useSharedState: 'wpndOverlaySelector',
      showIfSubTab: ['White Pine Needle Damage']
    },{
      component: 'radioSelector',
      id: 'beech-leaf-disease-overlay-type',
      useSharedState: 'beechLeafDiseaseOverlayTypeSelector',
      showIfSubTab: ['Beech Leaf Disease']
    },{
      component: 'radioSelector',
      id: 'hemlock-woolly-adelgid-overlay-type',
      useSharedState: 'hemlockWoollyAdelgidOverlayTypeSelector',
      showIfSubTab: ['Hemlock Woolly Adelgid']
    },{
      component: 'radioSelector',
      id: 'beech-leaf-disease-overlay-timeframe',
      useSharedState: 'beechLeafDiseaseOverlayTimeframeSelector',
      showIfSubTab: ['Beech Leaf Disease']
    },{
      component: 'thresholdSelector',
      id: 'biomass-threshold-selector',
      useSharedState: 'biomassThresholdSelector',
      props: {
        columnHeading: 'Biomass Threshold',
        minNum: 1,
        maxValue: Infinity,
        step: 0.5,
        colors: ['black'],
        label: () => 'Change Biomass Threshold',
        showReset: true,
        unit: '(tons/acre)',
        automaticUpdateOnEdit: false,
        precision: 1
    }
    }],
    chart: [{
      component: 'listSelector',
      id: 'pests-and-pathogens-charts',
      useSharedState: 'pestsAndPathogensChartSelector',
      props: {
        listTypeSingular: 'Chart',
        listTypePlural: 'Charts',
        showAll: false,
        showNone: false,
        label: () => 'Select Indicators',
        groupSx: { gap: '6px' }
      }
    },{
      component: 'thresholdSelector',
      id: 'southern-pine-beetle-threshold-selector',
      showIfSubTab: ['Southern Pine Beetle'],
      props: {
        columnHeading: 'Days Under',
        minNum: 1,
        maxNum: 1,
        minValue: -200,
        maxValue: 200,
        initValue: getInitTemp('Southern Pine Beetle'),
        resetValue: getInitTemp('Southern Pine Beetle'),
        step: 1,
        colors: ['black'],
        label: () => ['Change Temperature Threshold'],
        showReset: true,
        showHeader: false,
        endAdornment: '°F'
      }
    },{
      component: 'thresholdSelector',
      id: 'pine-wilt-disease-threshold-selector',
      showIfSubTab: ['Pine Wilt Disease'],
      props: {
        columnHeading: 'Days Under',
        minNum: 1,
        maxNum: 1,
        minValue: -200,
        maxValue: 200,
        initValue: getInitTemp('Pine Wilt Disease'),
        resetValue: getInitTemp('Pine Wilt Disease'),
        step: 1,
        colors: ['black'],
        label: () => ['Change Temperature Threshold', generateHoverPopper('icon', 'pwd-threshold')],
        showReset: true,
        showHeader: false,
        endAdornment: '°F'
      }
    },{
      component: 'thresholdSelector',
      id: 'heterobasidion-root-disease-threshold-selector',
      showIfSubTab: ['Heterobasidion Root Disease'],
      props: {
        columnHeading: 'Days Under',
        minNum: 1,
        maxNum: 1,
        minValue: -200,
        maxValue: 200,
        initValue: getInitTemp('Heterobasidion Root Disease'),
        resetValue: getInitTemp('Heterobasidion Root Disease'),
        step: 1,
        colors: ['black'],
        label: () => ['Change Temperature Threshold', generateHoverPopper('icon', 'hrd-threshold')],
        showReset: true,
        showHeader: false,
        endAdornment: '°F'
      }
    },{
      component: 'thresholdSelector',
      id: 'hemlock-woolly-adelgid-meanwt-threshold-selector',
      showIfSubTab: ['Hemlock Woolly Adelgid'],
      props: {
        columnHeading: 'Days Under',
        minNum: 1,
        maxNum: 1,
        minValue: -200,
        maxValue: 200,
        initValue: getInitTemp('Hemlock Wooly Adelgid MeanWT'),
        resetValue: getInitTemp('Hemlock Wooly Adelgid MeanWT'),
        step: 1,
        colors: ['black'],
        label: () => ['Change Mean Winter Temp. Threshold', generateHoverPopper('icon', 'hwa-meanwt-threshold')],
        showReset: true,
        showHeader: false,
        endAdornment: '°F'
      }
    },{
      component: 'thresholdSelector',
      id: 'hemlock-woolly-adelgid-meandt-threshold-selector',
      showIfSubTab: ['Hemlock Woolly Adelgid'],
      props: {
        columnHeading: 'Days Under',
        minNum: 1,
        maxNum: 1,
        minValue: -200,
        maxValue: 200,
        initValue: getInitTemp('Hemlock Wooly Adelgid MeanDT'),
        resetValue: getInitTemp('Hemlock Wooly Adelgid MeanDT'),
        step: 1,
        colors: ['black'],
        label: () => ['Change Min. Temp. Threshold', generateHoverPopper('icon', 'hwa-meandt-threshold')],
        showReset: true,
        showHeader: false,
        endAdornment: '°F'
      }
    },{
      component: 'thresholdSelector',
      id: 'hemlock-woolly-adelgid-mint-threshold-selector',
      showIfSubTab: ['Hemlock Woolly Adelgid'],
      props: {
        columnHeading: 'Days Under',
        minNum: 1,
        maxNum: 1,
        minValue: -200,
        maxValue: 200,
        initValue: getInitTemp('Hemlock Wooly Adelgid MinT'),
        resetValue: getInitTemp('Hemlock Wooly Adelgid MinT'),
        step: 1,
        colors: ['black'],
        label: () => ['Change Lowest Min. Temp. Threshold', generateHoverPopper('icon', 'hwa-mint-threshold')],
        showReset: true,
        showHeader: false,
        endAdornment: '°F'
      }
    },{
      component: 'seasonBoundsSelector',
      id: 'pests-and-pathogens-season-bounds-selector',
      useSharedState: 'pestsAndPathogensSeasonBoundsSelector',
      showIfSubTab: ['Southern Pine Beetle', 'Pine Wilt Disease', 'Heterobasidion Root Disease'],
    }]
  },
  componentFns: {
    title: updateTitle,
    aboveChartsTextBlock: ({ selectedSubTab }) => 'above-tool/' + selectedSubTab.toLowerCase().split(' ').join('-') + '.md',
    aboveMapsTextBlock: ({ selectedSubTab }) => 'above-tool/' + selectedSubTab.toLowerCase().split(' ').join('-') + '.md',
    mapTooltipFormatter: (value, { selectedSubTab, getSelectorValueByIdFromTabInformation }) => {
      if (selectedSubTab === 'White Pine Needle Damage') {
        const roundedValue = Math.round(value * 10) / 10;
        if (value < 1) {
          return `Healthy (${roundedValue})`;
        } else if (value < 2) {
          return `Mild Infection (${roundedValue})`;
        } else if (value < 3) {
          return `Moderate Infection (${roundedValue})`;
        } else {
          return `Severe Infection (${roundedValue})`;
        }
      } else if (selectedSubTab === 'Beech Leaf Disease') {
        return Math.round(value) + '%';
      } else if (selectedSubTab === 'Hemlock Woolly Adelgid') {
        const comparison = getSelectorValueByIdFromTabInformation('hemlockWoollyAdelgidOverlayTypeSelector');
        if (comparison === 'Current Year') {
          return value === 1 ? 'High Risk' : 'Low Risk';
        } else {
          const roundedValue = Math.round(value * 10) / 10;
          const units = ' years';
          return roundedValue + units;
        }
      } else {
        const comparison = getSelectorValueByIdFromTabInformation('departureSelector');
        if (comparison === 'Current Year') {
          return value < 1 ? 'High Risk' : 'Low Risk';
        } else {
          const roundedValue = Math.round(value * 10) / 10;
          const units = comparison.includes('(Days)') ? ' days' : '%';
          return roundedValue + units;
        }
      }
    } 
  },
  textPanelInfo: [{
    name: 'Introduction',
    showIfSubTab: ['Beech Leaf Disease'],
    markdownFilename: 'below-tool/beech-leaf-disease/introduction.md'
  },{
    name: 'Connection to Climate',
    showIfSubTab: ['Beech Leaf Disease'],
    markdownFilename: 'below-tool/beech-leaf-disease/connection-to-climate.md'
  },{
    name: 'Beech Leaf Disease Indicator Maps and Interpretation',
    showIfSubTab: ['Beech Leaf Disease'],
    markdownFilename: 'below-tool/beech-leaf-disease/indicator-maps-and-interpretation.md'
  },{
    name: 'Beech Leaf Disease Indicator Series and Interpretation',
    showIfSubTab: ['Beech Leaf Disease'],
    markdownFilename: 'below-tool/beech-leaf-disease/indicator-series-and-interpretation.md'
  },{
    name: 'Management Strategies for Beech Leaf Disease',
    showIfSubTab: ['Beech Leaf Disease'],
    markdownFilename: 'below-tool/beech-leaf-disease/management-strategies.md'
  },{
    name: 'About the Data',
    showIfSubTab: ['Beech Leaf Disease'],
    markdownFilename: 'below-tool/beech-leaf-disease/about-the-data.md'
  },{
    name: 'Additional Resources',
    showIfSubTab: ['Beech Leaf Disease'],
    markdownFilename: 'below-tool/beech-leaf-disease/additional-resources.md'
  },{

    name: 'Introduction',
    showIfSubTab: ['Heterobasidion Root Disease'],
    markdownFilename: 'below-tool/heterobasidion-root-disease/introduction.md'
  },{
    name: 'Connection to Climate',
    showIfSubTab: ['Heterobasidion Root Disease'],
    markdownFilename: 'below-tool/heterobasidion-root-disease/connection-to-climate.md'
  },{
    name: 'Heterobasidion Root Disease Indicator Series and Interpretation',
    showIfSubTab: ['Heterobasidion Root Disease'],
    markdownFilename: 'below-tool/heterobasidion-root-disease/indicator-series-and-interpretation.md'
  },{
    name: 'Management Strategies for Heterobasidion Root Disease',
    showIfSubTab: ['Heterobasidion Root Disease'],
    markdownFilename: 'below-tool/heterobasidion-root-disease/management-strategies.md'
  },{
    name: 'Additional Resources',
    showIfSubTab: ['Heterobasidion Root Disease'],
    markdownFilename: 'below-tool/heterobasidion-root-disease/additional-resources.md'
  },{

    name: 'Introduction',
    showIfSubTab: ['Pine Wilt Disease'],
    markdownFilename: 'below-tool/pine-wilt-disease/introduction.md'
  },{
    name: 'Connection to Climate',
    showIfSubTab: ['Pine Wilt Disease'],
    markdownFilename: 'below-tool/pine-wilt-disease/connection-to-climate.md'
  },{
    name: 'Pine Wilt Disease Indicator Maps and Interpretation',
    showIfSubTab: ['Pine Wilt Disease'],
    markdownFilename: 'below-tool/pine-wilt-disease/indicator-maps-and-interpretation.md'
  },{
    name: 'Pine Wilt Disease Indicator Series and Interpretation',
    showIfSubTab: ['Pine Wilt Disease'],
    markdownFilename: 'below-tool/pine-wilt-disease/indicator-series-and-interpretation.md'
  },{
    name: 'Management Strategies for Pine Wilt Disease',
    showIfSubTab: ['Pine Wilt Disease'],
    markdownFilename: 'below-tool/pine-wilt-disease/management-strategies.md'
  },{
    name: 'About the Data',
    showIfSubTab: ['Pine Wilt Disease'],
    markdownFilename: 'below-tool/pine-wilt-disease/about-the-data.md'
  },{
    name: 'Additional Resources',
    showIfSubTab: ['Pine Wilt Disease'],
    markdownFilename: 'below-tool/pine-wilt-disease/additional-resources.md'
  },{

    name: 'Introduction',
    showIfSubTab: ['Southern Pine Beetle'],
    markdownFilename: 'below-tool/southern-pine-beetle/introduction.md'
  },{
    name: 'Connection to Climate',
    showIfSubTab: ['Southern Pine Beetle'],
    markdownFilename: 'below-tool/southern-pine-beetle/connection-to-climate.md'
  },{
    name: 'Southern Pine Beetle Indicator Maps and Interpretation',
    showIfSubTab: ['Southern Pine Beetle'],
    markdownFilename: 'below-tool/southern-pine-beetle/indicator-maps-and-interpretation.md'
  },{
    name: 'Southern Pine Beetle Indicator Series and Interpretation',
    showIfSubTab: ['Southern Pine Beetle'],
    markdownFilename: 'below-tool/southern-pine-beetle/indicator-series-and-interpretation.md'
  },{
    name: 'Management Strategies for Southern Pine Beetle',
    showIfSubTab: ['Southern Pine Beetle'],
    markdownFilename: 'below-tool/southern-pine-beetle/management-strategies.md'
  },{
    name: 'About the Data',
    showIfSubTab: ['Southern Pine Beetle'],
    markdownFilename: 'below-tool/southern-pine-beetle/about-the-data.md'
  },{
    name: 'Additional Resources',
    showIfSubTab: ['Southern Pine Beetle'],
    markdownFilename: 'below-tool/southern-pine-beetle/additional-resources.md'
  },{

    name: 'Introduction',
    showIfSubTab: ['White Pine Needle Damage'],
    markdownFilename: 'below-tool/white-pine-needle-damage/introduction.md'
  },{
    name: 'Connection to Climate',
    showIfSubTab: ['White Pine Needle Damage'],
    markdownFilename: 'below-tool/white-pine-needle-damage/connection-to-climate.md'
  },{
    name: 'White Pine Needle Damage Indicator Maps and Interpretation',
    showIfSubTab: ['White Pine Needle Damage'],
    markdownFilename: 'below-tool/white-pine-needle-damage/indicator-maps-and-interpretation.md'
  },{
    name: 'White Pine Needle Damage Indicator Series and Interpretation',
    showIfSubTab: ['White Pine Needle Damage'],
    markdownFilename: 'below-tool/white-pine-needle-damage/indicator-series-and-interpretation.md'
  },{
    name: 'Management Strategies for White Pine Needle Damage',
    showIfSubTab: ['White Pine Needle Damage'],
    markdownFilename: 'below-tool/white-pine-needle-damage/management-strategies.md'
  },{
    name: 'About the Data',
    showIfSubTab: ['White Pine Needle Damage'],
    markdownFilename: 'below-tool/white-pine-needle-damage/about-the-data.md'
  },{
    name: 'Additional Resources',
    showIfSubTab: ['White Pine Needle Damage'],
    markdownFilename: 'below-tool/white-pine-needle-damage/additional-resources.md'
  },{

    name: 'Introduction',
    showIfSubTab: ['Hemlock Woolly Adelgid'],
    markdownFilename: 'below-tool/hemlock-woolly-adelgid/introduction.md'
  },{
    name: 'Connection to Climate',
    showIfSubTab: ['Hemlock Woolly Adelgid'],
    markdownFilename: 'below-tool/hemlock-woolly-adelgid/connection-to-climate.md'
  },{
    name: 'Hemlock Woolly Adelgid Indicator Maps and Interpretation',
    showIfSubTab: ['Hemlock Woolly Adelgid'],
    markdownFilename: 'below-tool/hemlock-woolly-adelgid/indicator-maps-and-interpretation.md'
  },{
    name: 'Hemlock Woolly Adelgid Indicator Series and Interpretation',
    showIfSubTab: ['Hemlock Woolly Adelgid'],
    markdownFilename: 'below-tool/hemlock-woolly-adelgid/indicator-series-and-interpretation.md'
  },{
    name: 'Management Strategies for Hemlock Woolly Adelgid',
    showIfSubTab: ['Hemlock Woolly Adelgid'],
    markdownFilename: 'below-tool/hemlock-woolly-adelgid/management-strategies.md'
  },{
    name: 'About the Data',
    showIfSubTab: ['Hemlock Woolly Adelgid'],
    markdownFilename: 'below-tool/hemlock-woolly-adelgid/about-the-data.md'
  },{
    name: 'Additional Resources',
    showIfSubTab: ['Hemlock Woolly Adelgid'],
    markdownFilename: 'below-tool/hemlock-woolly-adelgid/additional-resources.md'
  }],
  // pdfPanelInfo: [{
  //   showIfSubTab: ['Beech Leaf Disease'],
  //   pdfFilename: 'pdfs/FCI_BLD_Factsheet_Draft001.pdf'
  // }]
};
export default info;

export const pestsAndPathogensSharedStateDefinitions = {
  'pestsAndPathogensChartSelector': {
    options: info.chartInfo.charts,
    initValue: info.chartInfo.charts
  },
  'pestsAndPathogensSeasonBoundsSelector': {
    initValue: [calcInitStartDate('09-01', TODAY), calcInitEndDate('08-31', TODAY)],
    // initValue: [calcInitStartDate('09-01', TODAY), format(TODAY, 'yyyy-MM-dd')],
    props: {
      label: () => 'Change Chart Dates'
    }
  },
  'spbMortalitySelector': {
    initValue: 'Moderate (~75%, -4°F)',
    props: {
      options: ['Low (~50%, 0°F)', 'Moderate (~75%, -4°F)','High (>90%, -8°F)'],
      label: () => 'Beetle Mortality',
      title: 'Beetle Mortality'
    }
  },
  'wpndOverlaySelector': {
    initValue: 'Current Year',
    props: {
      options: ['Current Year', 'Last 30 Years'],
      label: () => 'Time Frame',
      title: 'Time Frame'
    }
  },
  'beechLeafDiseaseOverlayTypeSelector': {
    initValue: 'Isothermality',
    props: {
      options: ['Isothermality', 'Seasonality'],
      label: () => 'Variable',
      title: 'Variable'
    }
  },
  'beechLeafDiseaseOverlayTimeframeSelector': {
    initValue: 'Last Year',
    props: {
      options: ['Last Year', 'Normal'],
      label: () => 'Time Frame',
      title: 'Time Frame'
    }
  },
  'bldKnownCountiesToggle': {
    initValue: false,
    props: {
      label: 'Change County Overlay',
      ariaLabel: 'County overlay toggle',
      toggleSideLabels: ['Calculated from Biomass','Known Presence']
    }
  },
  'hwaKnownCountiesToggle': {
    initValue: false,
    props: {
      label: 'Change County Overlay',
      ariaLabel: 'County overlay toggle',
      toggleSideLabels: ['Calculated from Biomass','Known Presence']
    }
  },
  'hemlockWoollyAdelgidOverlayTypeSelector': {
    initValue: 'Current Year',
    props: {
      options: ['Current Year', 'Last 30 Years'],
      label: () => 'Time Frame',
      title: 'Time Frame'
    }
  }
};